import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box } from '@mui/material';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';

import { LostCargoSearchDrawer, LostCargoNotFoundPanel } from './components';

import TimeHelper from '../../utils/time';
import { PageUrl } from '../../types/enums';
import { LostCargoQuery } from '../../services/query';
import { cargoCompanyShipmentStates, ShippingListStatus } from './helper/enums';

import airdropShipment from '../../assets/icons/svg/pageIcons/airdropShipment.svg';
import { LostCargoCommand } from '../../services/command';
import { LOSTCARGO, lostCargoCls } from './lostCargo.classes';
import { executeLostCargoPrintRequests, LOST_PACKAGE_OVERDUE_DAYS } from './LostCargo.helper';

const backofficeUrl = import.meta.env.VITE_APPURL;

export function LostCargo() {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [gridData, setGridData] = useState([]);
    const [notFoundShipments, setNotFoundShipments] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [cargoCompanies, setCargoCompanies] = useState<any[]>([]);
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

    const { t } = useTranslation();

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    // Helper function to create parameters for print/cancel operation
    const createPrintCancelParams = (rowData: any, isPrint: boolean) => {
        const baseParams = {
            CustomerId: rowData.customerId,
            OrderId: rowData.cargoCompanyOrderId,
            ShippingListId: rowData.cargoCompanyShipmentId,
            OldStatus: rowData.cargoCompanyShipmentState,
        };

        if (!isPrint) {
            return {
                ...baseParams,
                NewStatus: ShippingListStatus.CANCELLED,
                CancelReasonId: 104,
            };
        } else {
            return {
                ...baseParams,
                NewStatus: ShippingListStatus.INVOICED_TO_CARGO_COMPANY,
            };
        }
    };

    // Helper function to handle successful print/cancel operation
    const handlePrintCancelSuccess = () => {
        Swal.fire({
            position: 'center',
            icon: 'success',
            title: t('general.success'),
            showConfirmButton: false,
            timer: 1500,
        });
        setIsLoading(false);
        setTriggerSearch(true);
    };

    // Helper function to handle print/cancel operation error
    const handlePrintCancelError = () => {
        setIsLoading(false);
        Swal.fire({
            title: t('general.anErrorOccured'),
            position: 'center',
            icon: 'error',
            showConfirmButton: false,
            timer: 3000,
        });
    };

    // Helper function to execute print/cancel operation
    const executePrintCancel = (rowData: any, isPrint: boolean) => {
        setIsLoading(true);
        const params = createPrintCancelParams(rowData, isPrint);

        LostCargoCommand.PrintOrCancel(params)
            .then((res: any) => {
                if (res) {
                    handlePrintCancelSuccess();
                }
            })
            .catch(handlePrintCancelError)
            .finally(() => {
                setIsLoading(false);
            });
    };

    const printOrCancelRow = (rowData: any, isPrint: boolean) => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: isPrint ? t('lostCargo.print') : t('lostCargo.cancel'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                executePrintCancel(rowData, isPrint);
            }
        });
    };

    const printSelectedRows = async () => {
        const result = await Swal.fire({
            text: t('general.sureText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('lostCargo.print'),
        });

        if (!result.isConfirmed) {
            return;
        }

        setIsLoading(true);

        try {
            const results = await executeLostCargoPrintRequests(selectedRows);
            const failedIds = results.filter(Boolean);

            if (failedIds.length > 0) {
                Swal.fire({
                    title: t('general.anErrorOccured'),
                    html: `<b>${t('lostCargo.failedShipments')}</b> ${failedIds.join(', ')}`,
                    icon: 'error',
                    showConfirmButton: true,
                });
            } else {
                setTriggerSearch(true);
                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: t('general.success'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
            setSelectedRows([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to create PDF parameters
    const createPdfParams = (rowData: any, fieldName: string) => {
        return {
            referenceId:
                fieldName === 'customerInvoiceNumber'
                    ? rowData.row.customerShipmentId
                    : rowData.row.cargoCompanyShipmentId,
            customerId:
                fieldName === 'customerInvoiceNumber' ? rowData.row.customerId : rowData.row.cargoCompanyCustomerId,
            isSellerStore: false,
        };
    };

    // Helper function to get filename for download
    const getDownloadFilename = (rowData: any, fieldName: string) => {
        return (
            (fieldName === 'customerInvoiceNumber'
                ? rowData.row.customerInvoiceNumber
                : rowData.row.cargoCompanyInvoiceNumber) || `invoice.pdf`
        );
    };

    // Helper function to download PDF from URL
    const downloadPdfFromUrl = (url: string, filename: string) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    // Helper function to process PDF document
    const processPdfDocument = (res: any, filename: string) => {
        try {
            const pdfData = atob(res.docPdf);
            const pdfArray = new Uint8Array(pdfData.length);
            for (let i = 0; i < pdfData.length; i++) {
                pdfArray[i] = pdfData.charCodeAt(i);
            }
            const pdfBlob = new Blob([pdfArray], { type: 'application/pdf' });
            const pdfUrl = URL.createObjectURL(pdfBlob);
            downloadPdfFromUrl(pdfUrl, filename);
            URL.revokeObjectURL(pdfUrl);
        } catch (error) {
            console.error('Error fetching PDF:', error);
        }
    };

    // Helper function to handle PDF response
    const handlePdfResponse = (res: any, rowData: any, fieldName: string) => {
        const filename = getDownloadFilename(rowData, fieldName);

        if (res.url) {
            downloadPdfFromUrl(res.url, filename);
        } else if (res.document) {
            processPdfDocument(res, filename);
        } else {
            Swal.fire(t('lostCargo.invoiceNotFound'), ' ', 'error');
        }
    };

    const getPdf = (rowData: any, fieldName: string) => {
        const params = createPdfParams(rowData, fieldName);

        LostCargoQuery.GetInvoicePDF(params)
            .then((res: any) => {
                handlePdfResponse(res, rowData, fieldName);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), ' ', 'error');
            });
    };

    const handleSelectionModelChange = (model: any[]) => {
        const selectedData = model.map((customerOrderId: any) => {
            return gridData.find((item: any) => item.customerOrderId === customerOrderId);
        });
        setSelectedRows(selectedData.filter(Boolean));
    };

    const selectAllPrintableRows = () => {
        const printableRows = (gridData || []).filter((item: any) => item.isPrintableCargoCompanyInvoice);
        setSelectedRows(printableRows);
    };

    const columnData: any = [
        {
            field: 'statusButtons',
            headerName: t('lostCargo.statusButtons'),
            headerAlign: 'center',
            align: 'center',
            width: 100,
            renderCell: (params: any) => {
                return (
                    <Box className={LOSTCARGO.datagridStatusButtonsWrap} sx={{ display: 'flex', flexDirection: 'column', padding: '4px' }}>
                        <SCUIButton
                            className={LOSTCARGO.inputButtonRowPrint}
                            type={'primary'}
                            style={{
                                marginBottom: '4px',
                                width: '50px',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0, 123, 255, 0.3)',
                            }}
                            sizeType={'fixed'}
                            disabled={!params.row.isPrintableCargoCompanyInvoice}
                            size={'s'}
                            text={t('lostCargo.print')}
                            onClick={() => printOrCancelRow(params.row, true)}
                        />
                        <SCUIButton
                            className={LOSTCARGO.inputButtonRowCancel}
                            type={'primary'}
                            style={{
                                backgroundColor: 'red',
                                width: '50px',
                                borderColor: 'red',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(255, 65, 54, 0.3) ',
                            }}
                            disabled={!params.row.isCancellableCargoCompanyShipment}
                            sizeType={'fixed'}
                            size={'s'}
                            text={t('lostCargo.cancel')}
                            onClick={() => printOrCancelRow(params.row, false)}
                        />
                    </Box>
                );
            },
        },
        {
            field: 'customerLostPackagePeriod',
            headerName: t('lostCargo.customerLostPackagePeriod'),
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const isOverdue = Number(params?.value) > LOST_PACKAGE_OVERDUE_DAYS;
                return (
                    <p style={{ fontSize: '12px', color: isOverdue ? '#d32f2f' : undefined, fontWeight: isOverdue ? 700 : undefined }}>
                        {`${params?.value} ${t('general.day')}`}
                    </p>
                );
            },
        },
        {
            field: 'cargoCompanyShipmentNumber',
            headerName: t('lostCargo.cargoCompanyShipmentNumber'),
            headerAlign: 'center',
            align: 'center',
            width: 150,
            renderCell: (params: any) => {
                return (
                    <a
                        target="_blank"
                        className={lostCargoCls('linktxt', LOSTCARGO.datagridLinkOrder)}
                        href={backofficeUrl + '/orderdetail?shippingListNo=' + params?.value}
                        rel="noreferrer"
                    >
                        {params?.value}
                    </a>
                );
            },
        },
        {
            field: 'cargoCompanyShipmentState',
            headerName: t('lostCargo.cargoCompanyShipmentState'),
            width: 200,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => {
                const label: any = cargoCompanyShipmentStates.find(
                    (option: any) => option.value === params.value
                )?.label;
                return <p> {t(label || '') || '-'}</p>;
            },
        },
        {
            field: 'cargoCompanyName',
            headerName: t('lostCargo.cargoCompanyName'),
            width: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'cargoCompanyInvoiceGrandTotal',
            headerName: t('lostCargo.cargoCompanyInvoiceGrandTotal'),
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return `${params?.value} ${params?.row.currencySymbol}`;
            },
        },
        {
            field: 'cargoCompanyInvoiceNumber',
            headerName: t('lostCargo.cargoCompanyInvoiceNumber'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return (
                    <p className={lostCargoCls('linktxt', LOSTCARGO.datagridLinkInvoiceCargo)} onClick={() => getPdf(params, 'cargoCompanyInvoiceNumber')}>
                        {params?.value}
                    </p>
                );
            },
        },
        {
            field: 'customerShipmentNumber',
            headerName: t('lostCargo.customerShipmentNumber'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return (
                    <a
                        target="_blank"
                        className={lostCargoCls('linktxt', LOSTCARGO.datagridLinkOrder)}
                        href={backofficeUrl + '/orderdetail?shippingListNo=' + params?.value}
                        rel="noreferrer"
                    >
                        {params?.value}
                    </a>
                );
            },
        },
        {
            field: 'customerShipmentState',
            headerName: t('lostCargo.customerShipmentState'),
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const label: any = cargoCompanyShipmentStates.find(
                    (option: any) => option.value === params.value
                )?.label;
                return <p> {t(label || '') || '-'}</p>;
            },
        },
        {
            field: 'customerInvoiceGrandTotal',
            headerName: t('lostCargo.customerInvoiceGrandTotal'),
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return `${params?.value} ${params?.row.currencySymbol}`;
            },
        },
        {
            field: 'customerInvoiceNumber',
            headerName: t('lostCargo.customerInvoiceNumber'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return (
                    <p className={lostCargoCls('linktxt', LOSTCARGO.datagridLinkInvoiceCustomer)} onClick={() => getPdf(params, 'customerInvoiceNumber')}>
                        {params?.value}
                    </p>
                );
            },
        },
        {
            field: 'customerShippedDate',
            headerName: t('lostCargo.customerShippedDate'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return TimeHelper.formatDateHour(params?.value || '-');
            },
        },
        {
            field: 'customerLostPackageDate',
            headerName: t('lostCargo.customerLostPackageDate'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return TimeHelper.formatDateHour(params?.value || '-');
            },
        },
    ];

    const getDrawerSelectData = () => {
        LostCargoQuery.GetCargoCompanies()
            .then((res: any) => {
                if (res) {
                    setCargoCompanies(res.data);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            });
    };

    const excelData = useMemo(() => {
        if ((gridData || []).length > 0) {
            return gridData.map((item: any) => {
                return {
                    lostCargoCargoCompanyShipmentNumber: item.cargoCompanyShipmentNumber,
                    lostCargoCargoCompanyShipmentDate: TimeHelper.formatDateHour(item.cargoCompanyShipmentDate),
                    lostCargoCargoCompanyShipmentState: t(
                        cargoCompanyShipmentStates.find(
                            (option: any) => option.value === item.cargoCompanyShipmentState
                        )?.label
                    ),
                    lostCargoCargoCompanyName: item.cargoCompanyName,
                    lostCargoCargoCompanyInvoiceGrandTotal: `${item.cargoCompanyInvoiceGrandTotal} ${item.currencySymbol}`,
                    lostCargoCargoCompanyInvoiceNumber: item.cargoCompanyInvoiceNumber,
                    lostCargoCustomerShipmentNumber: item.customerShipmentNumber,
                    lostCargoCustomerShipmentDate: TimeHelper.formatDateHour(item.customerShipmentDate),
                    lostCustomerShipmentState: t(
                        cargoCompanyShipmentStates.find((option: any) => option.value === item.customerShipmentState)
                            ?.label
                    ),
                    lostCargoCustomerShippedDate: TimeHelper.formatDateHour(item.customerShippedDate),
                    lostCargoCustomerLostPackageDate: TimeHelper.formatDateHour(item.customerLostPackageDate),
                    lostCargoCustomerLostPackagePeriod: `${item.customerLostPackagePeriod} ${t('general.day')}`,
                    lostCargoCustomerInvoiceGrandTotal: `${item.customerInvoiceGrandTotal} ${item.currencySymbol}`,
                    lostCargoCustomerInvoiceNumber: item.customerInvoiceNumber,
                };
            });
        }
        return [];
    }, [gridData]);

    useEffect(() => {
        getDrawerSelectData();
    }, []);

    return (
        <SCUIContent>
            <Box className={LOSTCARGO.page}>
                <div className={LOSTCARGO.loadingSection}>{isLoading ? <SCUILoadingLinear /> : null}</div>
                <div className={LOSTCARGO.breadcrumbSection}>
                    <SCUIBreadCrumb title={t('lostCargo.title')} componentLink={PageUrl.lostCargo.url} />
                </div>
            {isSearched ? (
                <>
                    <Box className={LOSTCARGO.resultsSection} sx={{ display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
                        <LostCargoNotFoundPanel notFoundShipments={notFoundShipments} />
                        <Box className={LOSTCARGO.toolbarRow} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
                            <SCUIButton
                                className={LOSTCARGO.inputButtonSelectAll}
                                text={t('lostCargo.selectAll')}
                                type="primary"
                                sizeType="fixed"
                                disabled={(gridData || []).length === 0}
                                size={'s'}
                                style={{ width: '150px', marginRight: '8px' }}
                                onClick={selectAllPrintableRows}
                            />
                            <SCUIButton
                                className={LOSTCARGO.inputButtonBulkPrint}
                                text={t('lostCargo.printBulk')}
                                type="primary"
                                sizeType="fixed"
                                disabled={
                                    selectedRows?.length === 0 ||
                                    selectedRows?.find((item: any) => !item.isPrintableCargoCompanyInvoice)
                                }
                                size={'s'}
                                style={{ width: '150px' }}
                                onClick={() => printSelectedRows()}
                            />
                            <Box className={LOSTCARGO.toolbarActionsEnd} sx={{ marginLeft: 'auto' }}>
                                <SCUIButton
                                    className={LOSTCARGO.inputButtonDetailedSearch}
                                    text={t('lostCargo.detailedSearch')}
                                    type="primary"
                                    sizeType="fixed"
                                    size={'s'}
                                    style={{ width: '150px', marginBottom: '16px', marginLeft: 'auto' }}
                                    onClick={() => setIsDrawerOpen(true)}
                                />
                                <Box className={LOSTCARGO.toolbarExcelWrap}>
                                    <SCUIDownloadButton
                                        position="right"
                                        excelData={{
                                            excelDownloadData: excelData,
                                            excelDownloadFolderName: t('excel.lostCargo'),
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            className={LOSTCARGO.gridWrap}
                            sx={{
                                [`& .${LOSTCARGO.rowOverdue}`]: {
                                    color: '#d32f2f',
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                },
                            }}
                        >
                            <SCUICustomDataGrid
                            onPageSizeChange={(i: number) => {
                                setPageNumber(0);
                                setPageSize(i);
                                if (isSearched) {
                                    setTriggerSearch(true);
                                }
                            }}
                            pageSize={pageSize}
                            rowHeight={100}
                            paginationModeChange
                            checkboxSelection
                            onSelectionModelChange={handleSelectionModelChange}
                            selectionModel={selectedRows.map((row) => row.customerOrderId)}
                            onPageChange={(i: number) => {
                                setPageNumber(i);
                                if (isSearched) {
                                    setTriggerSearch(true);
                                }
                            }}
                            page={pageNumber}
                            rowCount={totalCount}
                            getRowId={(row: any) => row.customerOrderId}
                            getRowClassName={(params: any) =>
                                Number(params?.row?.customerLostPackagePeriod) > LOST_PACKAGE_OVERDUE_DAYS
                                    ? LOSTCARGO.rowOverdue
                                    : ''
                            }
                            selectionOnClick={true}
                            rows={gridData || []}
                            columns={columnData}
                        />
                        </Box>
                    </Box>
                </>
            ) : (
                <Box className={LOSTCARGO.emptyStateWrap} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <SCUINoRecordComponent img={airdropShipment} />
                    <SCUIButton
                        className={LOSTCARGO.inputButtonEmptySearch}
                        style={{ width: '100px', marginTop: '16px' }}
                        text={t('general.search')}
                        type="primary"
                        sizeType="fixed"
                        size={'m'}
                        onClick={() => setIsDrawerOpen(true)}
                    />
                </Box>
            )}
            <LostCargoSearchDrawer
                isDrawerOpen={isDrawerOpen}
                setGridData={setGridData}
                setNotFoundShipments={setNotFoundShipments}
                setTotalCount={setTotalCount}
                handleDrawerClose={handleDrawerClose}
                cargoCompanies={cargoCompanies}
                setIsLoading={setIsLoading}
                setIsSearched={setIsSearched}
                triggerSearch={triggerSearch}
                setTriggerSearch={setTriggerSearch}
                pageSize={pageSize}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                setSelectedRows={setSelectedRows}
            />
            </Box>
        </SCUIContent>
    );
}
